// guided-make-chrome — the isolated "guided-make" surface (guided-make-activity, card
// sTMSPmGQ). Host chrome AROUND the unchanged runner: the student works through the
// teacher's ordered steps in their OWN editable working Snippet (Ace, readOnly:false), runs
// the code freely with the shared gold Run, and — advisory only — clicks Check to test the
// current source against the step's structural checks (guided-make-checks.js). A collapsible,
// default-collapsed model-solution reveal is offered on EVERY step (card fkop8tUe); when
// expanded it shows ONLY the current step's model line-range (modelFromLine/modelToLine),
// tracking Back/Next. The model source is server-sourced from the model-answer project (the
// project link never reaches the client).
//
// guided-make-failing-checks (Trello KITDDt8F): completion is a MaterialCompletion tick (NO
// server grade), decided the moment a run STARTED ON THE FINAL STEP reaches `running` (games
// loop forever) or a terminal state — but that run is no longer completed blind. EVERY step's
// checks are evaluated against the current source at that moment: all green (or the activity
// has none) completes exactly as before; any red HOLDS completion — nothing is recorded, the
// run is consumed, a "still to fix" list appears above Back/Next, and the final-step control
// offers "Finish without all checks" (which completes immediately, with no new run). Guided make stays
// deliberately NOT fail-able: a student can always finish. See guided-make-checks.js for the
// pure evaluator and openspec/changes/guided-make-failing-checks/design.md for the state
// machine this file implements.
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc, now }
//
// Autosave reuses the assignment path (editor-api.saveDraft on the working Snippet). Checks
// are ADVISORY — Back/Next are ALWAYS available and never gated. NO runner-contract change;
// editor-core untouched.

import { runChecks, runAllChecks } from './guided-make-checks.js';
import { inlineMarkdown } from './inline-markdown.js';
// tiered-hints (Trello 6WQKXSWl): the shared three-tier hint ladder renderer.
import { renderLadder, usesLadder } from './hint-ladder.js';
import { mountPreviewStrip } from './preview-strip.js';
// activity-retry-and-close-on-success (Trello 2eajTgm0): the close-on-success prompt.
import { offerClose, offerReturn, closePromptSpent, readReturnTarget } from './close-prompt.js';
import { registerSaveHandler } from './editor-shortcuts.js';

// one-tab-clarify-wording: the ending's lead. "Finish without all checks" must not be
// congratulated, so `endingLead()` picks between the two.
var ENDING_LEAD_BUILT = 'Nice work — you built it.';
var ENDING_LEAD_FAILING = 'You’ve finished, but some checks still need fixing.';

var AUTOSAVE_DEBOUNCE_MS = 1500;

// one-tab-same-tab-launches (Trello bD1oe35m): the wired page's flush, or null before `wire`
// reaches its autosave — challenge-chrome.js's contract exactly.
var activeFlush = null;

// Cancel the autosave debounce and save the live files NOW. Resolves the draft endpoint's reply
// (`{ success: true }` when it saved), rejects on a network/HTTP failure, and resolves `null` when
// no guided-make autosave is wired (a plan-locked page, an empty make). The activity top bar's
// Back awaits it.
export function flushPendingSave() {
  return activeFlush ? activeFlush() : Promise.resolve(null);
}

// EXPLICIT save state (see `assignment-chrome.js` for the full rule) — `pending` (debounce
// scheduled), `inflight` (save requests outstanding), `lastFailed` (the last save to finish
// rejected or resolved `success !== true`, cleared by the next success). The Images & sounds
// `working-assets` save is deliberately NOT tracked here (decisions.md, audit round 1).
var saveState = { wired: false, pending: false, inflight: 0, lastFailed: false };

export function hasPendingSave() {
  if (!saveState.wired) { return false; }
  return saveState.pending || saveState.inflight > 0 || saveState.lastFailed;
}

function trackSave(promise) {
  saveState.inflight += 1;
  return Promise.resolve(promise).then(function (res) {
    saveState.inflight = Math.max(0, saveState.inflight - 1);
    saveState.lastFailed = !(res && res.success === true);
    return res;
  }, function (err) {
    saveState.inflight = Math.max(0, saveState.inflight - 1);
    saveState.lastFailed = true;
    throw err;
  });
}

/** Test-only reset so module state does not leak between specs. */
export function __resetGuidedMakeSaveState() {
  activeFlush = null;
  saveState = { wired: false, pending: false, inflight: 0, lastFailed: false };
}
var TERMINAL_STATES = ['finished', 'stopped', 'student-error', 'runner-error'];
// guided-make-failing-checks: the final-step button / Go-to double-activation guard window
// (R14/R17/R20/R21). Timestamp-only — nothing cancels a window early (R38).
var GUARD_MS = 500;
// The rail's fail-mark X path, reused for the still-to-fix list's rows (R8: the SAME path,
// not a literal "✗" character).
var FAIL_ICON_PATH = '<path d="M18 6 6 18"/><path d="M6 6l12 12"/>';
// Exactly the four NET label transitions that start a guard window (design §4, R20/R25/R37).
// Any change TO "Next" (Back, or the step change behind Go-to) is deliberately absent.
var GUARD_FLIPS = {
  'Finish->Finish without all checks': true,
  'Next->Finish without all checks': true,
  'Finish without all checks->✓ Done': true,
  'Finish without all checks->Finish': true
};

// Read + parse the server-embedded config (already '<'-escaped by the handler).
function readConfig(doc) {
  var el = doc.getElementById('guided-make-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || '{}');
    if (!cfg || typeof cfg !== 'object') { return null; }
    return cfg;
  } catch (e) { return null; }
}

// The main-file source for the advisory checks (main.py, else the first file).
function mainSource(core) {
  var files;
  try { files = core.getFiles(); } catch (e) { files = []; }
  if (!Array.isArray(files) || !files.length) { return ''; }
  var main = files.filter(function (f) { return f && f.name === 'main.py'; })[0] || files[0];
  return (main && main.content) || '';
}

export function wire(ctx) {
  var doc  = ctx.doc;
  var core = ctx.core;
  var api  = ctx.editorApi;
  var cfg  = readConfig(doc);
  if (!cfg) { return; }

  // guided-make-failing-checks (R38): the guard clock. `ctx.now` is an optional function
  // returning ms, used only by specs; `main.js` passes nothing, so production is unaffected.
  function now() { return (typeof ctx.now === 'function') ? ctx.now() : Date.now(); }
  // guided-make-error-settle (Trello TmxL8sbx): a looping game reports `running` BEFORE its first frame
  // has raised, so deciding at once let a program that crashes in its first update() complete the make
  // (the `student-error` arrived a moment after the decision). A run that reaches `running` is therefore
  // decided after this short settle window (ms) unless a terminal state arrives first. Tests inject
  // `ctx.settleMs` (0 = decide immediately, the old behaviour) and `ctx.setTimeout` / `ctx.clearTimeout`.
  var SETTLE_MS = (typeof ctx.settleMs === 'number' && ctx.settleMs >= 0) ? ctx.settleMs : 1500;
  var setT   = (typeof ctx.setTimeout === 'function') ? ctx.setTimeout : function (fn, ms) { return setTimeout(fn, ms); };
  var clearT = (typeof ctx.clearTimeout === 'function') ? ctx.clearTimeout : function (id) { clearTimeout(id); };

  // teacher-preview-split (tasks 9.1/9.3): SERVER-RESOLVED preview mode. `cfg.preview` was
  // set only after the route's pre-handler confirmed `isCourseStaff` (R17), so a student who
  // opens a shared or bookmarked preview-flagged URL sees no strip and keeps persisting
  // exactly as today.
  //
  // teacher-preview-cleanup (finding 2): guided-make launches SAME-TAB in preview mode (R25), so
  // the exit NAVIGATES to the classroom URL supplied in `cfg.returnUrl` (preview flag already
  // stripped) instead of `history.back()`, which returned to that URL still carrying
  // `?preview=student`. Unlike the two embeds this surface's flag IS the `?preview=` query
  // param, so when no valid target is present a plain reload genuinely does leave preview —
  // hence `reloadFallback` rather than hiding the control.
  var previewMode = cfg.preview === true;
  // one-tab-same-tab-launches: the activity top bar's preview chip and "Back to class" replace
  // the strip, so it is mounted only when the bar is absent (card 2's guard).
  if (!doc.getElementById('surface-topbar-back')) {
    mountPreviewStrip(doc, {
      active: previewMode,
      exit: 'navigate',
      exitUrl: cfg.returnUrl || '',
      reloadFallback: true
    });
  }

  var panel = doc.getElementById('guided-make-panel');
  if (!panel) { return; }

  var steps     = Array.isArray(cfg.steps) ? cfg.steps : [];
  var model     = cfg.model || null;
  var canManage = !!cfg.canManage;
  // guided-make-model-note: an authored signpost that REPLACES the model-code reveal. The
  // server already decided this — when a note is set it never loads the model project, so
  // `cfg.model` is absent on this path and the two panels can never both exist. The trim is
  // defence in depth (the server trims too): a stray space in stored data must not silently
  // remove a stuck student's only remaining fallback.
  var modelNote = (typeof cfg.modelNote === 'string') ? cfg.modelNote.trim() : '';
  if (!steps.length) { return; }
  var lastIndex = steps.length - 1;

  // ── state ───────────────────────────────────────────────────────────────────
  var current            = 0;      // 0-based current step index
  // ranOnFinalStep: a run that STARTED on the final step reached `running` or a terminal
  // state. `startedOnFinalStep` LATCHES on `booting` — the qualifying step is the one the run
  // BEGAN on, not the one `running` happens to arrive on, so clicking Next during a boot that
  // started elsewhere cannot complete the activity early (guided-make-finish-gate). Both are
  // CLEARED the moment a run's checks are evaluated (guided-make-failing-checks, R1) — a run is
  // consumed exactly once, whether it completes or holds.
  var startedOnFinalStep = false;  // latched on `booting`: did THIS run begin on the final step?
  var ranOnFinalStep     = false;  // a run started on the final step reached running/terminal
  var completed           = false; // /complete has been POSTed (once)
  var settleTimer         = null;  // pending settle window for a run that reached `running`

  // guided-make-failing-checks: the check-evaluation hold.
  var holdActive  = false;  // a hold has happened this page visit; cleared on completion (R10)
  var offerAnyway = false;  // the final-step button currently offers "Finish without all checks" (R2)
  // guided-make-finish-gate (Trello kucivi12): a red check (or a final run that ended in a student
  // error) must be fixed before the make counts as finished — but the student must never get stuck.
  // Each held Finish is one try; once `failedFinishes` reaches the limit the override is offered
  // (the same idea as predict's attemptThreshold). Per page load; nothing is stored for the count.
  var finishTryLimit = (cfg && typeof cfg.attemptThreshold === 'number' && cfg.attemptThreshold >= 1)
    ? Math.floor(cfg.attemptThreshold) : 2;
  var failedFinishes = 0;
  var finishedWithFailures = false;  // the override was used: tell the server (teacher-visible mark)
  var failing     = [];     // the failing set currently rendered in the still-to-fix list
  // (R28's "unchanged set ⇒ don't rebuild"; reset on every emptying — R32 — so a return to the
  // same failing set refills the list)
  var navFlipAt    = -Infinity;  // guard window start for the final-step button (R20/R25/R37)
  var listRenderAt = -Infinity;  // guard window start for the list's Go-to buttons (R21)

  // ── close-on-success rendezvous (guided-make-finish-gate) ────────────────────────────────
  // The countdown must never take the tab while the student's program is still running, so its
  // second condition is a PREDICATE on the CURRENT runner state, not a remembered "did it ever
  // stop" boolean (decisions.md, "Panel corrections, round 3"). `lastRunnerState` is updated on
  // EVERY state event, `booting` and `running` included.
  var lastRunnerState = null;
  var postSettledOk   = false;  // the /complete POST has settled successfully
  var closeArmPending = false;  // eligible for the deferred close offer; one-shot in this module

  // ── DOM helpers ───────────────────────────────────────────────────────────────
  function el(tag, cls, text) {
    var n = doc.createElement(tag);
    if (cls) { n.className = cls; }
    if (text != null) { n.textContent = text; }
    return n;
  }
  function svg(paths, cls) {
    var n = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    n.setAttribute('viewBox', '0 0 24 24'); n.setAttribute('width', '16'); n.setAttribute('height', '16');
    n.setAttribute('fill', 'none'); n.setAttribute('stroke', 'currentColor'); n.setAttribute('stroke-width', '2');
    n.setAttribute('stroke-linecap', 'round'); n.setAttribute('stroke-linejoin', 'round');
    n.setAttribute('aria-hidden', 'true');
    if (cls) { n.setAttribute('class', cls); }
    n.innerHTML = paths;
    return n;
  }

  // guided-make-failing-checks: the final-step button's label as a PURE function of state, so
  // the double-activation guard can compare a "before" and "after" across one synchronous
  // action regardless of how many times renderNav() runs within it (R25).
  function currentFinalLabel() {
    if (current !== lastIndex) { return 'Next'; }
    if (completed) { return '✓ Done'; }
    if (offerAnyway) { return 'Finish without all checks'; }
    return 'Finish';
  }
  function stampGuardIfFlip(before, after) {
    if (before === after) { return; }
    if (GUARD_FLIPS[before + '->' + after]) { navFlipAt = now(); }
  }

  // ── build the panel ────────────────────────────────────────────────────────────
  var meta      = el('p', 'gm-step-meta');
  var stepLabel = el('h2', 'gm-step-label');
  var prompt    = el('div', 'gm-prompt');           // pre-wrap + inlineMarkdown (escape-first)

  var hintWrap   = el('div', 'gm-hint');
  var hintToggle = el('button', 'gm-hint-toggle');
  hintToggle.type = 'button';
  hintToggle.setAttribute('aria-expanded', 'false');
  hintToggle.appendChild(svg('<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>', 'gm-hint-icon'));
  hintToggle.appendChild(doc.createTextNode('Show hint'));
  var hintBody = el('div', 'gm-hint-body');
  hintBody.hidden = true;
  hintWrap.appendChild(hintToggle);
  hintWrap.appendChild(hintBody);
  // tiered-hints: the current step's ladder node (null on a legacy or hint-less step).
  var hintLadderNode = null;
  hintToggle.addEventListener('click', function () {
    var open = hintBody.hidden;
    hintBody.hidden = !open;
    hintToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    hintToggle.lastChild.nodeValue = open ? 'Hide hint' : 'Show hint';
  });

  // Advisory Check + ✓/✗ rail.
  var actions  = el('div', 'gm-actions');
  var checkBtn = el('button', 'gm-btn gm-btn-check', 'Check my code');
  checkBtn.type = 'button';
  var checkNote = el('p', 'gm-check-note', 'Checks are only a guide. You can always move on.');
  actions.appendChild(checkBtn);
  actions.appendChild(checkNote);
  var rail = el('ul', 'gm-checks');
  rail.hidden = true;

  checkBtn.addEventListener('click', function () {
    var step = steps[current] || {};
    var results = runChecks(mainSource(core), step.checks || []);
    renderRail(results);
    // guided-make-failing-checks (R6): Check on the final step, while a hold is active, ALSO
    // refreshes the still-to-fix list for DISPLAY only — it never records completion. Guarded
    // on `!completed` (R10: completion ends the hold for good).
    if (!completed && holdActive && current === lastIndex) {
      var before = currentFinalLabel();
      refresh(runAllChecks(mainSource(core), steps));
      stampGuardIfFlip(before, currentFinalLabel());
    }
  });

  function renderRail(results) {
    rail.textContent = '';
    if (!results.length) {
      rail.hidden = false;
      rail.appendChild(el('li', 'gm-check-empty', 'This step has no automatic checks — read the prompt and keep going.'));
      return;
    }
    results.forEach(function (r) {
      var li = el('li', 'gm-check-item ' + (r.passed ? 'is-pass' : 'is-fail'));
      var mark = r.passed
        ? svg('<path d="M20 6 9 17l-5-5"/>', 'gm-check-mark')
        : svg(FAIL_ICON_PATH, 'gm-check-mark');
      li.appendChild(mark);
      li.appendChild(el('span', 'gm-check-label', r.label));
      rail.appendChild(li);
    });
    rail.hidden = false;
  }

  // Step navigation.
  var nav     = el('div', 'gm-nav');
  var backBtn = el('button', 'gm-btn gm-btn-nav', 'Back');
  backBtn.type = 'button';
  var nextBtn = el('button', 'gm-btn gm-btn-nav gm-btn-next', 'Next');
  nextBtn.type = 'button';
  nav.appendChild(backBtn);
  nav.appendChild(nextBtn);
  backBtn.addEventListener('click', function () { if (current > 0) { goTo(current - 1); } });
  nextBtn.addEventListener('click', function () {
    if (current < lastIndex) { goTo(current + 1); return; }
    // guided-make-failing-checks (R20/R24): the guard is an early return only — no `disabled`,
    // no focus change (a keyboard user's focus must never be dropped to <body>).
    if (now() - navFlipAt < GUARD_MS) { return; }
    var before = currentFinalLabel();
    if (completed) {
      // Already complete: the same control is now "✓ Done", and it is the way OUT.
      finishUp();
    } else if (offerAnyway) {
      // guided-make-failing-checks (D6): "Finish without all checks" completes IMMEDIATELY, no new run —
      // it is only offered after the allowed tries were held (guided-make-finish-gate).
      finishedWithFailures = true;
      complete();
    } else {
      // On the final step "Next" becomes "Finish": a run is what marks the activity done
      // (formative), so trigger one — the state handler decides completion once that run
      // reaches `running` or a terminal state (guided-make-finish-gate).
      try { core.run(); } catch (e) { /* best-effort */ }
    }
    stampGuardIfFlip(before, currentFinalLabel());
  });

  // Nav button state. On the last step, "Next" is repurposed as a completion affordance:
  // an enabled "Finish" until a qualifying run is decided, then either a held "Finish without all checks"
  // or a solid green "✓ Done" — which STAYS ENABLED, because it is the student's way out.
  function renderNav() {
    backBtn.disabled = current === 0;
    var onLast = current === lastIndex;
    nextBtn.classList.toggle('gm-btn-done', onLast && completed);
    nextBtn.classList.toggle('gm-btn-anyway', onLast && !completed && offerAnyway);
    if (!onLast) {
      nextBtn.textContent = 'Next';
      nextBtn.disabled = false;
      nextBtn.title = '';
    } else if (completed) {
      // "✓ Done" is LIVE, not an ornament (Trello YXAF0iky follow-up). It used to be disabled,
      // which left the toolbar Stop as the only way to reach the close prompt — a hidden step,
      // and the same shape of complaint as the bug this surface was fixed for. Nothing is
      // blurred or disabled here any more, so the focus the student already has stays on the
      // control they are most likely to press next.
      nextBtn.textContent = '✓ Done';
      nextBtn.disabled = false;
      nextBtn.title = readReturnTarget() ? 'Go back to the lesson' : 'Go back to my classes';
    } else if (offerAnyway) {
      // guided-make-failing-checks: a qualifying run held because some check failed. The
      // control keeps `gm-btn-next` (R5) and gains the `gm-btn-anyway` modifier for its
      // secondary look — fixing is still the recommended action.
      nextBtn.textContent = 'Finish without all checks';
      nextBtn.disabled = false;
      nextBtn.title = '';
    } else {
      nextBtn.textContent = 'Finish';
      nextBtn.disabled = false;
      nextBtn.title = '';
    }
  }

  // Model-solution reveal (card fkop8tUe): mounted when a model source exists, AVAILABLE ON
  // EVERY STEP (default collapsed). The model code renders one line-row per source line; only
  // the active step's region (modelFromLine/modelToLine) is shown, and it tracks Back/Next via
  // highlightModelRegion(). The model source is server-sourced from the
  // model-answer project — the project link never reaches the client.
  // guided-make-model-note: `!modelNote` is defence in depth, not the mechanism. The server
  // already withholds `cfg.model` whenever a note is set (skipping the model-project load
  // entirely, so no source reaches the page at all), and this guard makes the mutual
  // exclusion local and readable: on the note path there is no code pane, no line rows and
  // no highlightModelRegion work. Exactly one of the two panels ever exists.
  var modelWrap     = null;
  var modelLineEls  = [];   // per-line row elements, index 0 == line 1
  if (!modelNote && model && model.code) {
    modelWrap = el('div', 'gm-model');
    modelWrap.hidden = true;
    var modelToggle = el('button', 'gm-model-toggle');
    modelToggle.type = 'button';
    modelToggle.setAttribute('aria-expanded', 'false');
    modelToggle.appendChild(svg('<path d="m9 18 6-6-6-6"/>', 'gm-model-chev'));
    modelToggle.appendChild(doc.createTextNode('Show a model solution'));
    var modelBody = el('div', 'gm-model-body');
    modelBody.hidden = true;
    var codePre = el('pre', 'gm-model-code');
    // Split into lines (drop a single trailing newline so we don't render a blank tail row).
    var srcLines = String(model.code).replace(/\n$/, '').split('\n');
    srcLines.forEach(function (text, i) {
      var lineEl = el('div', 'gm-model-line');
      lineEl.appendChild(el('span', 'gm-model-line-num', String(i + 1)));
      // A non-breaking space keeps an empty line's row height stable.
      lineEl.appendChild(el('span', 'gm-model-line-src', text.length ? text : ' '));
      codePre.appendChild(lineEl);
      modelLineEls.push(lineEl);
    });
    modelBody.appendChild(codePre);
    modelToggle.addEventListener('click', function () {
      var open = modelBody.hidden;
      modelBody.hidden = !open;
      modelToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      modelToggle.classList.toggle('is-open', open);
      modelToggle.lastChild.nodeValue = open ? 'Hide the model solution' : 'Show a model solution';
    });
    modelWrap.appendChild(modelToggle);
    modelWrap.appendChild(modelBody);
  }

  // guided-make-model-note: the SIGNPOST reveal — an authored note pointing back at the video
  // this build came from, offered INSTEAD of the model code. It gets its OWN wrapper variable
  // and its own append + goTo() handling ON PURPOSE: `modelWrap` is ALWAYS null on this path,
  // because the server ships no `cfg.model` when a note is set. Anything tucked inside an
  // `if (modelWrap)` block would therefore never run, and the note would never render at all.
  var noteWrap   = null;
  var noteToggle = null;
  var noteBody   = null;
  if (modelNote) {
    noteWrap = el('div', 'gm-note');
    noteWrap.hidden = true;
    noteToggle = el('button', 'gm-note-toggle');
    noteToggle.type = 'button';
    noteToggle.setAttribute('aria-expanded', 'false');
    noteToggle.appendChild(svg('<path d="m9 18 6-6-6-6"/>', 'gm-note-chev'));
    noteToggle.appendChild(doc.createTextNode('Where to find this'));
    noteBody = el('div', 'gm-note-body');
    noteBody.hidden = true;
    // The same escape-first, whitelist-second renderer the step prompt and hint use, so
    // authored **bold** reads as bold instead of literal asterisks. The note is per ACTIVITY,
    // not per step, so it is rendered ONCE here and never re-rendered in goTo().
    noteBody.innerHTML = inlineMarkdown(modelNote);
    noteWrap.appendChild(noteToggle);
    noteWrap.appendChild(noteBody);
    noteToggle.addEventListener('click', function () {
      var open = noteBody.hidden;
      noteBody.hidden = !open;
      noteToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      noteToggle.classList.toggle('is-open', open);
      noteToggle.lastChild.nodeValue = open ? 'Hide' : 'Where to find this';
    });
  }

  // Re-collapse the note reveal — the note panel's twin of collapseModel(), called from the
  // same place in goTo() so a fresh step starts un-revealed.
  function collapseNote() {
    if (!noteWrap) { return; }
    noteBody.hidden = true;
    noteToggle.setAttribute('aria-expanded', 'false');
    noteToggle.classList.remove('is-open');
    noteToggle.lastChild.nodeValue = 'Where to find this';
  }

  // Re-collapse the model reveal. Called on every step change so a fresh step starts
  // un-revealed — the student gets a chance to work it out before peeking at the answer.
  function collapseModel() {
    if (!modelWrap) { return; }
    modelBody.hidden = true;
    modelToggle.setAttribute('aria-expanded', 'false');
    modelToggle.classList.remove('is-open');
    modelToggle.lastChild.nodeValue = 'Show a model solution';
  }

  // Per-step reveal: show ONLY the current step's model lines (modelFromLine..modelToLine),
  // so the model solution never leaks the whole program at once. A range-less manual step
  // falls back to the whole model. A missing/partial range or one past the file end simply
  // shows nothing extra — never crashes (best-effort, D5).
  function highlightModelRegion(step) {
    if (!modelLineEls.length) { return; }
    var from = step && step.modelFromLine;
    var to   = step && step.modelToLine;
    var hasRange = (typeof from === 'number' && isFinite(from))
                && (typeof to === 'number' && isFinite(to));
    modelLineEls.forEach(function (lineEl, i) {
      var lineNo = i + 1;
      var inRange = hasRange && lineNo >= from && lineNo <= to;
      lineEl.hidden = hasRange && !inRange;
    });
  }

  // ── guided-make-failing-checks: the still-to-fix list ────────────────────────────────────
  // Built once here, at wire time — an `aria-live="polite"` region, NEVER `hidden` (R7): it is
  // emptied via `textContent = ''` whenever there is nothing to show, so the container is
  // always present in the accessibility tree before its text changes (the `.gm-complete-status`
  // precedent). Appended immediately before `panel.appendChild(nav)` below.
  var fixList = el('div', 'gm-fix');
  fixList.setAttribute('aria-live', 'polite');

  // Focus lives on a "Go to step N" button while the list is rebuilt or emptied for any OTHER
  // reason (R27) — captured by `data-step`, which every group and its go-to button both carry.
  function focusedGotoStep() {
    var ae = doc.activeElement;
    if (!ae || !fixList.contains(ae)) { return null; }
    return (ae.getAttribute) ? ae.getAttribute('data-step') : null;
  }
  function findGotoButton(stepIndex) {
    var groups = fixList.children || [];
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      if (g.getAttribute && g.getAttribute('data-step') === String(stepIndex)) {
        var kids = g.children || [];
        for (var j = 0; j < kids.length; j++) {
          if (kids[j].className && kids[j].className.indexOf('gm-fix-goto') !== -1) {
            return kids[j];
          }
        }
      }
    }
    return null;
  }

  // "Go to step N" (D10/R9/R21/R34): ignored within ~500ms of the list's own render (a
  // double-click on Finish must not land on a row the list just pushed under the cursor).
  // Its own list-emptying uses `clearFixListRaw()` — deliberately BYPASSING the generic R27
  // focus fallback below, because this path has its own explicit destination (`stepLabel`).
  function makeGotoHandler(stepIndex) {
    return function () {
      if (now() - listRenderAt < GUARD_MS) { return; }
      clearFixListRaw();
      goTo(stepIndex);
      var checks = (steps[stepIndex] || {}).checks || [];
      renderRail(runChecks(mainSource(core), checks));
      stepLabel.setAttribute('tabindex', '-1');
      // F2: `preventScroll` so the browser's own focus-scroll never moves the page — the
      // panel-scoped `scrollRailIntoView()` below does the (panel-only) scrolling instead.
      stepLabel.focus({ preventScroll: true });
      scrollRailIntoView();
    };
  }

  // Off-DOM build, ONE insertion pass (R28: the polite region announces it as a single unit).
  function buildFixListNodes(failures) {
    var nodes = [];
    var n = failures.length;
    var heading = n === 1
      ? 'Nearly there — 1 thing still needs fixing'
      : 'Nearly there — ' + n + ' things still need fixing';
    nodes.push(el('h3', 'gm-fix-heading', heading));

    var order = [];
    var byStep = {};
    failures.forEach(function (f) {
      if (!byStep[f.stepIndex]) {
        byStep[f.stepIndex] = { stepIndex: f.stepIndex, items: [] };
        order.push(byStep[f.stepIndex]);
      }
      byStep[f.stepIndex].items.push(f);
    });

    order.forEach(function (group) {
      var groupEl = el('div', 'gm-fix-group');
      groupEl.setAttribute('data-step', String(group.stepIndex));
      group.items.forEach(function (f) {
        var row = el('div', 'gm-fix-item');
        row.appendChild(svg(FAIL_ICON_PATH, 'gm-fix-mark'));
        // checker-diagnostics B3: the check label is the primary text, and a secondary line
        // beneath it says which step asked for it (the "Step N · " prefix is gone so the
        // step number is not printed twice).
        var labelEl = el('span', 'gm-fix-label', f.label);
        if (!f.runError) {
          // A real space before the block-level sub line, so the row's text (and its
          // accessible name) reads "<label> Step N asked…" rather than running together.
          labelEl.appendChild(doc.createTextNode(' '));
          labelEl.appendChild(el('span', 'gm-fix-sub',
            'Step ' + (group.stepIndex + 1) + ' asked for this. We looked for: ' +
            String(f.label).replace(/\.$/, '') + '.'));
        }
        row.appendChild(labelEl);
        groupEl.appendChild(row);
      });
      // D10: the final step's own failures get no jump control — the student is already there.
      if (group.stepIndex !== lastIndex) {
        var gotoBtn = el('button', 'gm-fix-goto', 'Go to step ' + (group.stepIndex + 1));
        gotoBtn.type = 'button';
        gotoBtn.setAttribute('data-step', String(group.stepIndex));
        gotoBtn.addEventListener('click', makeGotoHandler(group.stepIndex));
        groupEl.appendChild(gotoBtn);
      }
      nodes.push(groupEl);
    });

    // guided-make-finish-gate: the "you can still finish" sentence is true only once the button reads
    // "Finish without all checks" (offerAnyway); before that Finish holds again, so say only what to do.
    var footer = offerAnyway
      ? (n === 1
        ? 'Fix it and run your code again — or, if you’re sure it’s right, you can still finish.'
        : 'Fix them and run your code again — or, if you’re sure it’s right, you can still finish.')
      : (n === 1
        ? 'Fix it, then run your code again and press Finish.'
        : 'Fix them, then run your code again and press Finish.');
    nodes.push(el('p', 'gm-fix-footer', footer));

    return nodes;
  }

  // Plain empty — no focus handling. Used by the Go-to path above (which has its own focus
  // destination) and internally by `updateFixList([])`.
  function clearFixListRaw() {
    fixList.textContent = '';
    failing = [];  // R32: every emptying resets `failing`, so a same-set return refills it
  }

  // The general rebuild/empty entry point (R27/R28/R32): skips the DOM rebuild when the
  // failing set is unchanged from what is already shown; otherwise preserves focus that was
  // resting on a "Go to step N" button for the SAME step, else moves it to `nextBtn`.
  //
  // F3 (build-review round 9): `opts.force` bypasses the "unchanged ⇒ skip" short-circuit.
  // R28's no-rebuild rule exists for the DISPLAY-ONLY refreshes (D8 arrival, R6 Check click),
  // where an identical set means nothing new to announce. A qualifying-run HOLD is a distinct
  // event every time — the student ran their code again — so it always re-announces and
  // restarts the Go-to guard, even when the failing set happens to match.
  function updateFixList(failures, opts) {
    failures = failures || [];
    var force = !!(opts && opts.force);
    if (!force && JSON.stringify(failures) === JSON.stringify(failing)) { return; }
    var focusedStep = focusedGotoStep();
    clearFixListRaw();
    if (failures.length) {
      var nodes = buildFixListNodes(failures);
      nodes.forEach(function (node) { fixList.appendChild(node); });
      failing = failures;
      listRenderAt = now();
    }
    if (focusedStep != null) {
      var btn = failures.length ? findGotoButton(focusedStep) : null;
      (btn || nextBtn).focus();
    }
  }

  // guided-make-failing-checks: scroll helpers are PANEL-scoped, never `scrollIntoView` (R29 —
  // on the ≤900px stacked layout it would drag a just-started game off-screen), and NO-OP
  // (never throw) when layout metrics are unavailable, as in the hand-rolled test DOM (R35).
  //
  // F2 (build-review round 9): positions are computed from `getBoundingClientRect()`
  // DIFFERENCES against `.gm-panel`'s own rect, plus `panel.scrollTop` — never `offsetTop`.
  // `.gm-panel` has no positioned ancestor, so `offsetTop` resolves relative to the nearest
  // positioned ancestor (or the body), not to the scrolling panel; on this surface that silently
  // computed a body-relative offset while treating it as panel-relative.
  function scrollHoldIntoView() {
    try {
      if (!panel || typeof panel.getBoundingClientRect !== 'function'
        || typeof panel.scrollTop !== 'number' || typeof panel.clientHeight !== 'number') { return; }
      if (typeof fixList.getBoundingClientRect !== 'function'
        || typeof nav.getBoundingClientRect !== 'function') { return; }
      var panelRect = panel.getBoundingClientRect();
      var listTop   = fixList.getBoundingClientRect().top - panelRect.top + panel.scrollTop;
      var navBottom = nav.getBoundingClientRect().bottom - panelRect.top + panel.scrollTop;
      var viewH = panel.clientHeight;
      var target = (navBottom - listTop <= viewH) ? listTop : (navBottom - viewH);
      panel.scrollTop = target < 0 ? 0 : target;
    } catch (e) { /* R35: never throw */ }
  }
  function scrollRailIntoView() {
    try {
      if (!panel || typeof panel.getBoundingClientRect !== 'function'
        || typeof panel.scrollTop !== 'number' || typeof panel.clientHeight !== 'number') { return; }
      if (typeof rail.getBoundingClientRect !== 'function') { return; }
      var panelRect  = panel.getBoundingClientRect();
      var railRect   = rail.getBoundingClientRect();
      var railTop    = railRect.top - panelRect.top + panel.scrollTop;
      var railBottom = railRect.bottom - panelRect.top + panel.scrollTop;
      var viewTop    = panel.scrollTop;
      var viewBottom = viewTop + panel.clientHeight;
      if (railBottom > viewBottom) { panel.scrollTop = railBottom - panel.clientHeight; }
      else if (railTop < viewTop) { panel.scrollTop = railTop; }
    } catch (e) { /* R35: never throw */ }
  }

  panel.appendChild(meta);
  panel.appendChild(stepLabel);
  panel.appendChild(prompt);
  panel.appendChild(hintWrap);
  panel.appendChild(actions);
  panel.appendChild(rail);
  if (modelWrap) { panel.appendChild(modelWrap); }
  // guided-make-model-note: the note takes the model reveal's SLOT — appended here, BEFORE
  // the nav. `.gm-nav` carries `margin-top: auto`, so a panel appended after it would render
  // below Back/Next instead of above them.
  if (noteWrap) { panel.appendChild(noteWrap); }
  // guided-make-failing-checks (R35): APPENDED in order immediately before the nav — never
  // `insertBefore`, which the hand-rolled test DOM does not honour.
  panel.appendChild(fixList);
  panel.appendChild(nav);

  if (canManage) {
    panel.appendChild(el('div', 'gm-preview-note',
      'Preview mode — you are a teacher, so nothing you do here is recorded.'));
  }

  var saveStatus = el('p', 'gm-save-status', '');
  saveStatus.setAttribute('aria-live', 'polite');
  panel.appendChild(saveStatus);

  // Completion's OWN confirmation line (guided-make-finish-gate): "Marked as done in your
  // progress." now lands while the student is still typing, and gm-save-status's own
  // "Editing…" autosave overwrite (~1.5s later) would erase it within one keystroke — so it
  // gets a separate, persistent, aria-live element instead. Empty + tabindex="-1" at wire time;
  // focus only moves here after a successful POST writes its text (see complete()).
  var completeStatus = el('p', 'gm-complete-status', '');
  completeStatus.setAttribute('aria-live', 'polite');
  completeStatus.setAttribute('tabindex', '-1');
  panel.appendChild(completeStatus);

  // ── render a step ───────────────────────────────────────────────────────────
  function goTo(index) {
    // guided-make-failing-checks (R20/R25/R37): captured BEFORE `current` changes, so the
    // "before" label on arrival at the final step is always what was actually on screen (on a
    // non-final step, always "Next" — never an intermediate render within this same action).
    var beforeLabel = currentFinalLabel();
    current = index;
    var step = steps[current] || {};
    meta.textContent = 'Step ' + (current + 1) + ' of ' + steps.length;
    stepLabel.textContent = step.label || ('Step ' + (current + 1));
    prompt.innerHTML = inlineMarkdown(step.prompt);

    // tiered-hints: a step with tier 2 or 3 gets a FRESH ladder (collapsed, nothing revealed)
    // in the same wrap on every step change; the legacy toggle/body are hidden. A Hint-1-only
    // step runs today's code below untouched.
    if (hintLadderNode) {
      hintWrap.removeChild(hintLadderNode);
      hintLadderNode = null;
      hintToggle.hidden = false;
    }
    if (usesLadder(step)) {
      hintWrap.hidden = false;
      hintToggle.hidden = true;
      hintBody.hidden = true;
      hintLadderNode = renderLadder(doc, step, {
        renderText : function (target, text) { target.innerHTML = inlineMarkdown(text); }
      });
      hintWrap.appendChild(hintLadderNode);
    }
    // Hint (reset collapsed each step).
    else if (step.hint) {
      hintWrap.hidden = false;
      hintBody.innerHTML = inlineMarkdown(step.hint);
      hintBody.hidden = true;
      hintToggle.setAttribute('aria-expanded', 'false');
      hintToggle.lastChild.nodeValue = 'Show hint';
    } else {
      hintWrap.hidden = true;
    }

    // Fresh step ⇒ fresh rail (checks are per-step, cleared on Back/Next).
    rail.hidden = true;
    rail.textContent = '';
    var hasChecks = Array.isArray(step.checks) && step.checks.length;
    checkBtn.hidden = !hasChecks;
    checkNote.hidden = !hasChecks;

    // Nav availability + last-step Finish/Done state.
    renderNav();

    // The model reveal is available on EVERY step (card fkop8tUe) and RE-COLLAPSES on each step
    // change, so the student works the new step before peeking; it shows only that step's region.
    if (modelWrap) {
      modelWrap.hidden = false;
      collapseModel();
      highlightModelRegion(step);
    }

    // guided-make-model-note: a SEPARATE guard, deliberately not merged with the one above —
    // `modelWrap` is null whenever a note is set, so folding this into that block would mean
    // the note never unhides. The note itself is per activity, so only its collapsed state is
    // reset here; its text is rendered once at wire time.
    if (noteWrap) {
      noteWrap.hidden = false;
      collapseNote();
    }

    // guided-make-failing-checks (R7/D9): the list is shown only on the final step. Leaving it
    // (or arriving on it via a route other than "Go to step N", which clears it itself) empties
    // it; arrival re-fills it, if anything, via maybeComplete() below.
    if (current !== lastIndex) {
      updateFixList([]);
    }

    maybeComplete({ arrival: true });

    stampGuardIfFlip(beforeLabel, currentFinalLabel());
  }

  // ── the completion decision (guided-make-failing-checks, design §3) ─────────────────────
  // Distinguishes a runner-state call (no `opts`) from an ARRIVAL call from `goTo()` (R23: a
  // display-only re-evaluation can only ever be triggered by navigation, never a state event).
  function maybeComplete(opts) {
    var arrival = !!(opts && opts.arrival);
    if (arrival) { cancelSettle(); }   // R3: a pending qualifying run is decided on arrival
    if (completed) { return; }               // R10: nothing below runs once complete
    if (current !== lastIndex) { return; }
    if (ranOnFinalStep) {
      // R1: the WHOLE run is consumed here — both flags — so only a NEW `booting` on the final
      // step can ever qualify again; this run's later state events cannot re-decide it.
      ranOnFinalStep = false;
      startedOnFinalStep = false;
      var evalResult = runAllChecks(mainSource(core), steps);
      // A final run that ended in a student error does not count as finished either, even when
      // every structural check is green: it is held with its own plain-words entry.
      if (lastRunnerState === 'student-error') {
        evalResult = {
          failures: evalResult.failures.concat([{ stepIndex: lastIndex, runError: true,
            label: 'Your program stopped with an error. Fix it, then run it again.' }]),
          results: evalResult.results
        };
      }
      if (evalResult.failures.length === 0) { complete(); } else { hold(evalResult); }
    } else if (arrival && holdActive) {
      // D8: arrival re-evaluation is DISPLAY ONLY — it never records completion.
      refresh(runAllChecks(mainSource(core), steps));
    }
  }

  // A qualifying run whose evaluation found at least one failing check: completion is HELD.
  // One that found none instead COMPLETES (see `maybeComplete()` above) — never both. Nothing
  // is recorded; the still-to-fix list appears; the final-step control becomes "Finish without all checks";
  // a currently-visible rail is re-rendered from the SAME evaluation so the two can never
  // disagree (R13/R31); the panel scrolls so the list and Back/Next stay together on screen
  // (R16/R22/R29). F3: the list is FORCE-rebuilt — a qualifying run is a distinct event every
  // time (the student ran their code again), so it re-announces and restarts the Go-to guard
  // even when the failing set happens to be unchanged from the last hold.
  function hold(evalResult) {
    holdActive = true;
    // guided-make-finish-gate: this held Finish is one try. The override appears only once the
    // student has used up the allowed tries.
    failedFinishes += 1;
    offerAnyway = failedFinishes >= finishTryLimit;
    updateFixList(evalResult.failures, { force: true });
    if (!rail.hidden) { renderRail(evalResult.results[lastIndex] || []); }
    renderNav();
    scrollHoldIntoView();
  }

  // Display-only re-evaluation (D8 arrival, R6 Check-on-final-step): never records completion,
  // never scrolls (R16). All green removes the list and returns the control to plain "Finish".
  function refresh(evalResult) {
    if (evalResult.failures.length === 0) {
      updateFixList([]);
      offerAnyway = false;
    } else {
      offerAnyway = failedFinishes >= finishTryLimit;
      updateFixList(evalResult.failures);
    }
    if (!rail.hidden) { renderRail(evalResult.results[lastIndex] || []); }
    renderNav();
  }

  // The ONE shared completion path (R4/D6): a green qualifying run and "Finish without all checks" both
  // land here. Extracted, byte-for-byte, from what `maybeComplete()` used to do unconditionally.
  function complete() {
    completed = true;
    holdActive = false;
    offerAnyway = false;
    updateFixList([]);
    renderNav();  // flip the last-step button to "✓ Done"
    // teacher-preview-split (task 9.2 / D6): completion affordances stay LIVE in preview
    // mode — the tick flips — but record NOTHING. This is an explicit client guard, not a
    // reliance on the server's roster 403.
    if (previewMode) { return; }
    var url = '/api/courses/' + cfg.courseId + '/lessons/' + cfg.lessonId
            + '/materials/' + cfg.materialId + '/complete';
    // per-class-material-progress: stamp the class cohort key (launch URL → cfg) when present.
    var payloadObj = {};
    if (cfg.linkedCourseId) { payloadObj.linkedCourseId = cfg.linkedCourseId; }
    // guided-make-finish-gate: the teacher-visible "finished with checks failing" mark.
    if (finishedWithFailures) { payloadObj.checksFailing = true; }
    var body = JSON.stringify(payloadObj);
    try {
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: body
      }).then(function (res) {
        // A teacher preview (canManage) is 403'd by the roster course-student guard —
        // expected + silent.
        if (res && res.ok && !canManage) {
          // The aria-live region announces this on its own. Focus is deliberately NOT moved
          // here: "✓ Done" is now an enabled control and the student's next action, so pulling
          // focus off it into a static line would leave Enter doing nothing.
          completeStatus.textContent = 'Marked as done in your progress.';
        }
        // activity-retry-and-close-on-success: offer the way back only AFTER the completion
        // tick has settled. Navigating with it in flight aborts it and records nothing.
        offerAfterComplete(!!(res && res.ok));
      }).catch(function () { offerAfterComplete(false); });
    } catch (e) { offerAfterComplete(false); }
  }

  // activity-retry-and-close-on-success (D42/R19): never for a teacher, never over a rejected
  // save, and NEVER for a student who had already finished this build before they opened it.
  //
  // That last one is the whole reason `cfg.alreadyComplete` exists as a separate suppression
  // flag: this surface has no completion hydration, so a returning student's "Finish" runs the
  // program, re-fires the completion path and would have the tab taken away from them. The
  // flag is server-resolved and suppressed in preview, exactly like every other hydration.
  //
  // one-tab-same-tab-launches: guided-make launches in the classroom's OWN tab for everyone, so
  // every ending here asks for the same-tab return (`sameTab: true`).
  //
  // guided-make-finish-gate: the failure warn is NOT deferred — it fires the moment the POST
  // fails, with no wait for anything. Deferring it would hide the only failure signal behind a
  // terminal state that a pgzero or `input()`-blocked student never reaches, leaving them
  // looking at a green "✓ Done" that is a lie. The success countdown IS deferred, until the
  // rendezvous below also finds the program has stopped, so the tab is never taken away while
  // it is still running.
  function offerAfterComplete(saved) {
    if (!saved) {
      if (previewMode || canManage || cfg.alreadyComplete === true) { return; }
      offerReturn({ sameTab: true, warn: true, label: 'We couldn’t save that. Please tell your teacher.' });
      return;
    }
    postSettledOk = true;
    closeArmPending = true;
    tryArmClose();
  }

  // "The program has stopped" — a PREDICATE on the CURRENT runner state, not a remembered
  // event. Asking "is it running NOW?" (rather than "did it ever stop?") is what keeps a stale
  // pre-completion error, or a re-run started while the POST is still in flight, from arming
  // the countdown over a program that is live on screen (decisions.md, "round 3 (final)").
  function programHasStopped() {
    return TERMINAL_STATES.indexOf(lastRunnerState) !== -1;
  }

  // ONE shared arm function, called from both rendezvous trigger points below (the POST
  // settling, and a state event once `completed` is already true) — a duplicated check pasted
  // at only one site would pass half the suppression cases and fail the other half. Re-applies
  // the same suppression `offerAfterComplete`'s single call site used to carry: splitting the
  // function creates a SECOND path to `offerClose`, and `cfg.alreadyComplete` is precisely the
  // flag that stops a returning student who already finished from having their tab taken away.
  // `previewMode` cannot reach here — `complete()` already returned before the POST for a
  // previewing teacher.
  function tryArmClose() {
    if (!closeArmPending) { return; }
    if (canManage || cfg.alreadyComplete === true) { closeArmPending = false; return; }
    if (!postSettledOk || !programHasStopped()) { return; }
    closeArmPending = false;
    offerClose({ sameTab: true, label: endingLead() });
  }

  function endingLead() { return finishedWithFailures ? ENDING_LEAD_FAILING : ENDING_LEAD_BUILT; }

  // Pressing "✓ Done" (Trello YXAF0iky follow-up). The countdown is deliberately deferred until
  // the program stops, so a student whose pgzero window is still looping would otherwise have to
  // discover the toolbar Stop to get out — a hidden step, which is the complaint this surface
  // already exists to fix. Done ends the build itself.
  function finishUp() {
    var wasSpent = closePromptSpent();
    // Stop first: a game build is still looping, and the deferred rendezvous is waiting for
    // exactly that terminal state. The `stopped` event reaches tryArmClose() through the state
    // handler. A no-op on a program that has already ended.
    try { core.stop(); } catch (e) { /* best-effort */ }
    // ...and try directly too, for the console build that had ALREADY terminated: no further
    // state event follows, so the handler's call would never come. `closeArmPending` is
    // tryArmClose's own one-shot, so the two routes cannot double-fire.
    tryArmClose();

    // The countdown is never offered to a teacher (activity-close-on-success), and a preview
    // never completed anything to leave.
    if (previewMode || canManage) { return; }

    // Will anything actually appear? Only if an arm is still pending — it fires when the stop
    // above lands — or if one just armed here. Every other case means pressing Done would do
    // nothing visible, and a control that does nothing is the whole complaint this change
    // exists to fix. The live ones: a completion whose save was REFUSED or is still in flight,
    // and a prompt already spent earlier in the visit.
    //
    // one-tab-same-tab-launches (decisions.md, plan-review rounds 3 + 4 — the newest wins): a
    // save that settled SUCCESSFULLY gets the same-tab "Nice work — you built it." return; a failed or
    // unfinished save shows NO message (any failure warning has already been shown once) and
    // moves focus to the activity top bar's Back, so the press visibly lands on the way out —
    // or, when the bar is absent (its lookup failed and the page fell back to the navbar), the
    // neutral same-tab return. Never a success message after a failed or unfinished save.
    var willOffer = closeArmPending || (!wasSpent && closePromptSpent());
    if (willOffer) { return; }
    if (postSettledOk) {
      offerReturn({ sameTab: true, label: endingLead() });
      return;
    }
    var topbarBack = doc.getElementById('surface-topbar-back');
    if (topbarBack) {
      try { topbarBack.focus(); } catch (e) { /* focus is best-effort */ }
    } else {
      offerReturn({ sameTab: true });
    }
  }

  // ── runner events ────────────────────────────────────────────────────────────
  // guided-make-finish-gate: `lastRunnerState` is assigned FIRST — the rendezvous predicate
  // reads it — then the qualifying-run latch/flag, then `maybeComplete()`, then the arm.
  // Evaluating the arm before the assignment would read the PREVIOUS run's state and, in the
  // settle-first ordering, strand the countdown permanently because no further event follows.
  core.on('state', function (state) {
    lastRunnerState = state;

    if (state === 'booting') {
      // `booting` starts every run, including a restart — it is the signal for "which step did
      // THIS run begin on". It never itself sets the completion flag.
      startedOnFinalStep = (current === lastIndex);
      cancelSettle();   // a new run supersedes any pending decision
      ranOnFinalStep = false;   // ...including a previous run that had reached `running` but not yet been decided
    } else if (startedOnFinalStep
        && (state === 'running' || TERMINAL_STATES.indexOf(state) !== -1)) {
      // Counts `running` (games loop forever) alongside the four terminal states (formative —
      // an errored run still counts as "tried it"), but ONLY for a run that STARTED on the
      // final step — never test `current` at the moment this event arrives.
      ranOnFinalStep = true;
    }

    // guided-make-error-settle: `running` only STARTS the settle window; a terminal state (an early
    // student-error included) decides at once, with the error visible to maybeComplete().
    if (state === 'running' && ranOnFinalStep && !completed && SETTLE_MS > 0) {
      armSettle();
      return;
    }
    if (TERMINAL_STATES.indexOf(state) !== -1) { cancelSettle(); }

    decideNow();
  });

  function cancelSettle() {
    if (settleTimer != null) { clearT(settleTimer); settleTimer = null; }
  }
  function armSettle() {
    if (settleTimer != null) { return; }   // already settling this run
    settleTimer = setT(function () {
      settleTimer = null;
      decideNow();
    }, SETTLE_MS);
  }
  // The decision step shared by a runner-state event and the settle timer.
  function decideNow() {
    // guided-make-failing-checks (R20/R25): a runner-state action can flip the final-step
    // label (a held qualifying run, or a fresh green one completing) just as a click can.
    var beforeLabel = currentFinalLabel();
    maybeComplete();
    stampGuardIfFlip(beforeLabel, currentFinalLabel());

    if (completed) { tryArmClose(); }
  }

  // ── autosave-to-Draft (the assignment path) ───────────────────────────────────
  var draftId = doc.body.getAttribute('data-draft-project-id')
             || doc.body.getAttribute('data-project-id') || '';
  var saveTimer = null;
  function scheduleSave() {
    if (saveTimer) { clearTimeout(saveTimer); }
    saveStatus.textContent = 'Editing…';
    saveState.pending = true;
    saveTimer = setTimeout(function () {
      saveTimer = null;
      saveState.pending = false;
      saveStatus.textContent = 'Saving…';
      trackSave(api.saveDraft(draftId, core.getFiles()))
        .then(function () { saveStatus.textContent = 'Saved'; })
        .catch(function (err) { saveStatus.textContent = 'Save failed'; console.error(err); });
    }, AUTOSAVE_DEBOUNCE_MS);
  }
  var editor = core.getEditor();
  if (editor && editor.on) { editor.on('change', scheduleSave); }
  // one-tab-same-tab-launches: the save state is live from here.
  saveState.wired = true;

  // one-tab-same-tab-launches: the exported flush (see `flushPendingSave` above).
  activeFlush = function () {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    saveState.pending = false;
    saveStatus.textContent = 'Saving…';
    return trackSave(api.saveDraft(draftId, core.getFiles()))
      .then(function (res) {
        var ok = !!(res && res.success === true);
        saveStatus.textContent = ok ? 'Saved' : 'Save failed';
        return res;
      }, function (err) { saveStatus.textContent = 'Save failed'; throw err; });
  };

  // Ctrl-S / Cmd-S flushes the pending autosave immediately and saves unconditionally. The
  // handler writes its own status through the closure-held element — there is no
  // #gm-save-status id to look up, `gm-save-status` is a CLASS on a JS-built <p>, which is
  // exactly why the shortcut routes through a registered handler rather than a shared saver.
  var ctrlSaving = false;
  registerSaveHandler(function () {
    if (ctrlSaving) { return; }
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    saveState.pending = false;
    ctrlSaving = true;
    saveStatus.textContent = 'Saving…';
    trackSave(api.saveDraft(draftId, core.getFiles()))
      .then(function () { saveStatus.textContent = 'Saved'; })
      .catch(function (err) { saveStatus.textContent = 'Save failed'; console.error(err); })
      .then(function () { ctrlSaving = false; });
  });

  // First render.
  goTo(0);
}
